import type { ApiErrorBody } from '@crm/shared';
import { toast } from './motion';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }

  /** Field-level messages from a 422 response, keyed by field name. */
  fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    return Object.fromEntries((this.details as { field: string; message: string }[]).map((d) => [d.field, d.message]));
  }
}

/** When the server last answered (the "session about to end" warning counts idle time from here). */
export let lastServerContact = Date.now();
let reachable = true;
/** Tells the connection bar (AppShell) when requests stop reaching the server, and when they get through again. */
function netState(ok: boolean) {
  if (ok === reachable || typeof window === 'undefined') return;
  reachable = ok;
  window.dispatchEvent(new CustomEvent('crm-connection', { detail: ok }));
}

/** quiet: no message after the save (used by Undo, which shows its own). */
export async function api<T>(path: string, init: { method?: string; body?: unknown; headers?: Record<string, string>; quiet?: boolean } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      headers: { ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}), ...init.headers },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch (e) {
    // No answer at all (weak signal, offline): the connection bar shows it.
    netState(false);
    throw e;
  }
  netState(true);
  lastServerContact = Date.now();
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  // Not JSON (a proxy's HTML page while the server restarts during an install): a clear message, not "Unexpected token '<'".
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch {
    throw new ApiError(res.status, 'bad_response', res.ok ? 'Unexpected reply from the server. Try again.' : `The server is unavailable right now (${res.status}). Try again in a moment.`);
  }
  if (!res.ok) {
    const e = (data as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, e?.code ?? 'error', e?.message ?? `Request failed (${res.status})`, e?.details);
  }
  const done = init.quiet ? null : actionMessage(init.method ?? 'GET', path, init.body);
  if (done) toast(done);
  return data as T;
}

const NOUN: Record<string, string> = { opportunity: 'Deal', account: 'Company', contact: 'Contact', lead: 'Lead' };
/** The message a record save shows (so a screen can add Undo to the same message). */
export const savedText = (object: string) => `${NOUN[object] ?? 'Record'} saved`;
export const deletedText = (object: string) => `${NOUN[object] ?? 'Record'} deleted`;
/** The short message shown after a save that changes a record or an activity (nothing for reads and searches). */
function actionMessage(method: string, path: string, body: unknown): string | null {
  const m = method.toUpperCase();
  if (m === 'GET') return null;
  const p = path.split('?')[0]!;
  const b = (body ?? {}) as Record<string, unknown>;
  let r = /^\/api\/records\/([a-z_]+)(\/[^/]+)?$/.exec(p);
  if (r) {
    const noun = NOUN[r[1]!] ?? 'Record';
    // A new record opens on its page, which already says it was created.
    if (m === 'POST' && !r[2]) return null;
    if (m === 'PATCH' && r[2]) return 'stageId' in b ? 'Stage changed' : `${noun} saved`;
    if (m === 'DELETE' && r[2]) return `${noun} deleted`;
    return null;
  }
  r = /^\/api\/activities(\/[^/]+)?$/.exec(p);
  if (r) {
    if (m === 'POST' && !r[1]) return b.kind === 'event' ? 'Meeting created' : b.status === 'completed' ? 'Activity logged' : 'Task created';
    if (m === 'PATCH' && r[1]) return b.status === 'completed' ? 'Task done' : 'Activity saved';
    if (m === 'DELETE' && r[1]) return 'Activity deleted';
  }
  return null;
}

export const qs = (params: Record<string, string | number | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
};

/** The message to show for a failed request: the server's own words, else `fallback`. */
export const errorText = (e: unknown, fallback = 'Something went wrong.'): string => (e instanceof ApiError ? e.message : fallback);

/** The same, also showing a browser-side error's message (a file that couldn't be read, a network failure). */
export const errorDetail = (e: unknown, fallback = 'Something went wrong.'): string =>
  e instanceof ApiError ? e.message : e instanceof Error && e.message ? e.message : fallback;
